<template>
  <div class="banner-table">
    <SoDataTable
      :headers="headers"
      :items="items"
      :searchable="false"
      :has-pagination="false"
      :select-key="editable ? 'name' : 'variantKey'"
      :no-data-text="noDataText"
      @dragover="dragOver"
      @drop.prevent="dropped = true"
    >
      <template v-if="title" #tableActions>
        <div
          class="d-flex align-center justify-space-between flex-grow-1 heading-row"
        >
          <span class="text-h6" data-testid="placement-title">{{ title }}</span>
          <div v-if="editable && hasChanges">
            <SoButton
              title="Cancel"
              class="mr-2"
              large
              :disabled="isSaving"
              data-testid="banner-changes-cancel"
              @click="discardChanges"
            />
            <SoButton
              title="Review & Save"
              type="success"
              large
              :disabled="isSaving"
              data-testid="banner-changes-review"
              @click="confirming = true"
            />
          </div>
        </div>
      </template>
      <template #[`item.position`]="{ internalItem }">
        <div
          v-if="editable"
          class="d-flex align-center ga-1"
          :class="{
            'banner-dragging': dragging === asGroup(internalItem.raw).name,
          }"
          :data-banner-name="asGroup(internalItem.raw).name"
        >
          <span
            v-if="canDrag && !isSaving"
            class="drag-handle"
            draggable="true"
            title="Drag to reorder"
            data-testid="banner-drag-handle"
            @dragstart="startDrag(asGroup(internalItem.raw), $event)"
            @dragend="endDrag"
          >
            <SoIcon icon="grip-vertical" />
          </span>
          <SoNumberField
            :model-value="priorityShown(asGroup(internalItem.raw))"
            :aria-label="`Priority of ${asGroup(internalItem.raw).name}`"
            :min="0"
            :step="1"
            :precision="0"
            :disabled="isSaving"
            control-variant="hidden"
            hide-details
            class="priority-field"
            data-testid="banner-priority"
            @update:model-value="
              setPriorityDraft(asGroup(internalItem.raw), $event)
            "
            @blur="commitPriority(asGroup(internalItem.raw))"
            @keydown.enter="commitPriority(asGroup(internalItem.raw))"
          />
        </div>
        <span v-else class="font-weight-bold" data-testid="banner-position">
          {{ asRow(internalItem.raw).position ?? "—" }}
        </span>
      </template>
      <template #[`item.enabled`]="{ internalItem }">
        <SoToggle
          :model-value="enabledShown(asGroup(internalItem.raw))"
          :disabled="isSaving"
          :aria-label="`Show ${asGroup(internalItem.raw).name}`"
          name="banner-enabled"
          data-testid="banner-enabled"
          @update:model-value="stageEnabled(asGroup(internalItem.raw), $event)"
        />
      </template>
      <template #[`item.banner`]="{ internalItem }">
        <div class="banner-cell">
          <div
            class="font-weight-bold one-line"
            :title="bannerOf(internalItem.raw).title"
            data-testid="banner-title"
          >
            {{ bannerOf(internalItem.raw).title }}
          </div>
          <div
            class="text-body-2 two-lines"
            :title="bannerOf(internalItem.raw).body"
            data-testid="banner-body"
          >
            {{ bannerOf(internalItem.raw).body }}
          </div>
          <span
            v-if="editable && isSwitched(asGroup(internalItem.raw))"
            class="d-sr-only banner-changed"
            data-testid="banner-unsaved"
            >Unsaved change</span
          >
        </div>
      </template>
      <template #[`item.button`]="{ internalItem }">
        <div class="button-cell">
          <div class="one-line" data-testid="banner-button-text">
            {{ bannerOf(internalItem.raw).buttonText ?? "—" }}
          </div>
          <SoLink
            v-if="bannerOf(internalItem.raw).buttonHref"
            :href="bannerOf(internalItem.raw).buttonHref ?? undefined"
            :text="bannerOf(internalItem.raw).buttonHref ?? ''"
            :title="bannerOf(internalItem.raw).buttonHref ?? undefined"
            class="text-caption one-line d-block"
            data-testid="banner-link"
          />
          <code
            v-else-if="bannerOf(internalItem.raw).buttonLink"
            class="text-caption one-line d-block"
            :title="bannerOf(internalItem.raw).buttonLink ?? undefined"
            data-testid="banner-link"
            >{{ bannerOf(internalItem.raw).buttonLink }}</code
          >
        </div>
      </template>
      <template #[`item.illustration`]="{ internalItem }">
        <code class="text-caption" data-testid="banner-illustration">{{
          bannerOf(internalItem.raw).illustrationName
        }}</code>
      </template>
      <template #[`item.platforms`]="{ internalItem }">
        <div class="d-flex flex-wrap ga-1">
          <SoChip
            v-for="platform in bannerOf(internalItem.raw).platforms"
            :key="platform"
            :text="platform"
            size="small"
            data-testid="banner-platform"
          />
        </div>
      </template>
      <template #[`item.name`]="{ internalItem }">
        <div class="d-flex flex-column align-start ga-1">
          <code class="text-caption" data-testid="banner-name">{{
            bannerOf(internalItem.raw).name
          }}</code>
          <SoChip
            v-if="versionsText(internalItem.raw)"
            :text="versionsText(internalItem.raw) ?? ''"
            size="small"
            data-testid="banner-versions"
          />
          <SoChip
            v-if="editable && asGroup(internalItem.raw).rowsDisagree"
            :type="SoChipType.WARNING"
            text="Out of step"
            title="Its copies differ — switch it or set its priority, then save"
            size="small"
            data-testid="banner-rows-disagree"
          />
        </div>
      </template>
      <template v-if="editable" #itemActions="{ internalItem }">
        <SoButton
          title="Edit"
          type="table-action"
          :disabled="isSaving"
          data-testid="banner-edit"
          @click="$emit('edit', asGroup(internalItem.raw))"
        />
      </template>
    </SoDataTable>

    <MarketingBannerConfirmDialog
      v-model="confirming"
      title="Save banner changes?"
      :changes="changeRows"
      :notes="changeNotes"
      :loading="isSaving"
      @confirm="saveChanges"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoLink from "@/components/SoLink.vue"
  import SoNumberField from "@/components/formControls/SoNumberField.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import {
    IBannerGroup,
    IBannerPriorityChange,
    IBannerRow,
    IMarketingBanner,
  } from "@/models/alertsBanners/alertsBanners.types"
  import { movedPriorities } from "@/models/alertsBanners/alertsBanners"
  import { AlertsBannersMutator } from "@/mutators/alertsBanners/AlertsBannersMutator"
  import { IMarketingBannerPatch } from "@/mutators/alertsBanners/AlertsBannersMutator.types"
  import { useSnackbarStore } from "@/stores/snackbar"
  import {
    bannerNotes,
    orderNotes,
  } from "@/views/admin/marketingBanners/marketingBannerNotes"
  import MarketingBannerConfirmDialog from "../modals/MarketingBannerConfirmDialog.vue"

  interface IStagedChange {
    group: IBannerGroup
    patch: Pick<IMarketingBannerPatch, "enabled" | "priority">
  }

  const isGroup = (raw: unknown): raw is IBannerGroup =>
    typeof raw === "object" && raw !== null && "rowIds" in raw

  const byPriority = (
    groups: IBannerGroup[],
    priorityOf: (group: IBannerGroup) => number,
  ): IBannerGroup[] =>
    groups
      .map((group, index) => ({ group, index }))
      .sort(
        (a, b) =>
          priorityOf(a.group) - priorityOf(b.group) || a.index - b.index,
      )
      .map(({ group }) => group)

  const describeChange = (
    { patch }: IStagedChange,
    savedPosition: number,
  ): string => {
    const parts: string[] = []
    if (patch.enabled !== undefined)
      parts.push(patch.enabled ? "Switch on" : "Switch off")
    if (patch.priority !== undefined)
      parts.push(`Priority ${savedPosition} → ${patch.priority}`)
    return parts.join(", ")
  }

  export default defineComponent({
    components: {
      SoDataTable,
      SoButton,
      SoChip,
      SoIcon,
      SoLink,
      SoNumberField,
      SoToggle,
      MarketingBannerConfirmDialog,
    },
    props: {
      editable: {
        type: Boolean,
        default: false,
      },
      rows: {
        type: Array as PropType<IBannerRow[]>,
        default: () => [],
      },
      groups: {
        type: Array as PropType<IBannerGroup[]>,
        default: () => [],
      },
      noDataText: {
        type: String,
        default: "No banners",
      },
      title: {
        type: String,
        default: undefined,
      },
      search: {
        type: String,
        default: "",
      },
    },
    emits: ["edit"],
    data() {
      return {
        SoChipType,
        stagedEnabled: {} as Record<string, boolean>,
        stagedPriority: {} as Record<string, number>,
        priorityDrafts: {} as Record<string, number | null>,
        dragging: null as string | null,
        dropped: false,
        priorityBeforeDrag: {} as Record<string, number>,
        moved: [] as string[],
        movedBeforeDrag: [] as string[],
        confirming: false,
        isSaving: false,
      }
    },
    computed: {
      headers(): SoDataTableHeader[] {
        return [
          { key: "position", title: "#", sortable: false },
          {
            key: "enabled",
            title: "Enabled",
            sortable: false,
            isVisible: this.editable,
          },
          { key: "banner", title: "Banner", sortable: false },
          { key: "button", title: "Button", sortable: false },
          { key: "illustration", title: "Illustration", sortable: false },
          { key: "platforms", title: "Platforms", sortable: false },
          { key: "name", title: "Name", sortable: false },
        ]
      },
      orderedGroups(): IBannerGroup[] {
        return byPriority(this.groups, this.priorityOf)
      },
      savedGroups(): IBannerGroup[] {
        return byPriority(this.groups, (group) => group.priority)
      },
      shiftedOnly(): IStagedChange[] {
        return this.changes.filter(
          ({ group, patch }) =>
            patch.enabled === undefined &&
            patch.priority !== group.priority &&
            !this.moved.includes(group.name),
        )
      },
      order(): IBannerPriorityChange[] {
        return this.orderedGroups.map((group) => ({
          name: group.name,
          priority: this.priorityOf(group),
        }))
      },
      // A filtered list hides a banner's real neighbours, so dragging is off while searching.
      canDrag(): boolean {
        return !this.search.trim()
      },
      // SoDataTable types its rows loosely; each one is an IBannerGroup or IBannerRow.
      items(): SoDataTableItem[] {
        return (this.editable
          ? this.orderedGroups.filter((group) => group.matches(this.search))
          : this.rows) as unknown as SoDataTableItem[]
      },
      // Out-of-step rows count as changed even at the same value, so re-saving fixes them.
      changes(): IStagedChange[] {
        return this.groups.flatMap((group) => {
          const patch: IStagedChange["patch"] = {}
          const enabled = this.stagedEnabled[group.name]
          const priority = this.stagedPriority[group.name]
          if (
            enabled !== undefined &&
            (enabled !== group.enabled || group.rowsDisagree)
          )
            patch.enabled = enabled
          if (
            priority !== undefined &&
            (priority !== group.priority || group.rowsDisagree)
          )
            patch.priority = priority
          return Object.keys(patch).length ? [{ group, patch }] : []
        })
      },
      hasChanges(): boolean {
        return this.changes.length > 0
      },
      changeNotes(): string[] {
        return this.changes.flatMap(({ group, patch }) =>
          (patch.enabled === undefined
            ? orderNotes(group)
            : bannerNotes(group)
          ).map((note) => `${group.name}: ${note}`),
        )
      },
      changeRows(): string[][] {
        const shifted = this.shiftedOnly.length
        const listed = this.changes.filter(
          (change) => !this.shiftedOnly.includes(change),
        )
        const rows = listed.map((change) => [
          change.group.name,
          describeChange(change, this.savedGroups.indexOf(change.group)),
        ])
        return shifted
          ? [
              ...rows,
              [
                `${shifted} other banner${shifted === 1 ? "" : "s"}`,
                "Shift to make room",
              ],
            ]
          : rows
      },
    },
    methods: {
      asRow(raw: unknown): IBannerRow {
        return raw as IBannerRow
      },
      asGroup(raw: unknown): IBannerGroup {
        return raw as IBannerGroup
      },
      bannerOf(raw: unknown): IMarketingBanner {
        return isGroup(raw) ? raw.primary : (raw as IBannerRow).banner
      },
      versionsText(raw: unknown): string | null {
        if (isGroup(raw)) {
          const labels = raw.versionLabels
          return labels.length > 1
            ? `${labels.length} versions: ${labels.join(", ")}`
            : null
        }
        const { name } = (raw as IBannerRow).banner
        const count =
          this.groups.find((group) => group.name === name)?.rows.length ?? 1
        return count > 1 ? `1 of ${count} versions of ${name}` : null
      },
      priorityOf(group: IBannerGroup): number {
        return this.stagedPriority[group.name] ?? group.priority
      },
      enabledShown(group: IBannerGroup): boolean {
        return this.stagedEnabled[group.name] ?? group.enabled
      },
      priorityShown(group: IBannerGroup): number | null {
        return group.name in this.priorityDrafts
          ? this.priorityDrafts[group.name]
          : this.orderedGroups.indexOf(group)
      },
      isSwitched(group: IBannerGroup): boolean {
        return this.changes.some(
          (change) =>
            change.group.name === group.name &&
            change.patch.enabled !== undefined,
        )
      },
      stageEnabled(group: IBannerGroup, enabled: boolean) {
        this.stagedEnabled = { ...this.stagedEnabled, [group.name]: enabled }
      },
      setPriorityDraft(group: IBannerGroup, value: number | null) {
        this.priorityDrafts = { ...this.priorityDrafts, [group.name]: value }
      },
      commitPriority(group: IBannerGroup) {
        if (!(group.name in this.priorityDrafts)) return
        const priority = this.priorityDrafts[group.name]
        this.priorityDrafts = Object.fromEntries(
          Object.entries(this.priorityDrafts).filter(
            ([name]) => name !== group.name,
          ),
        )
        if (priority === null || !Number.isInteger(priority)) return
        this.stageOrder(group, priority)
      },
      startDrag(group: IBannerGroup, event: DragEvent) {
        this.dragging = group.name
        this.dropped = false
        this.priorityBeforeDrag = this.stagedPriority
        this.movedBeforeDrag = this.moved
        if (!event.dataTransfer) return
        event.dataTransfer.effectAllowed = "move"
        // Not text/plain, or the search box would accept the drop and type the name in.
        event.dataTransfer.setData("application/x-marketing-banner", group.name)
        const row = (event.target as HTMLElement).closest("tr")
        if (row) event.dataTransfer.setDragImage(row, 16, row.offsetHeight / 2)
      },
      dragOver(event: DragEvent) {
        if (!this.dragging) return
        event.preventDefault()
        if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
        const over = (event.target as HTMLElement)
          .closest("tr")
          ?.querySelector<HTMLElement>("[data-banner-name]")?.dataset.bannerName
        const dragged = this.orderedGroups.find((g) => g.name === this.dragging)
        const target = this.orderedGroups.findIndex((g) => g.name === over)
        if (dragged && target !== -1 && over !== this.dragging)
          this.stageOrder(dragged, target)
      },
      // Escape or a drop outside the table puts the list back as it was.
      endDrag() {
        if (!this.dropped) {
          this.stagedPriority = this.priorityBeforeDrag
          this.moved = this.movedBeforeDrag
        }
        this.dragging = null
      },
      stageOrder(group: IBannerGroup, to: number) {
        const moves = movedPriorities(this.order, group.name, to)
        if (moves.length && !this.moved.includes(group.name))
          this.moved = [...this.moved, group.name]
        this.stagedPriority = {
          ...this.stagedPriority,
          [group.name]: this.priorityOf(group),
          ...Object.fromEntries(moves.map((m) => [m.name, m.priority])),
        }
      },
      discardChanges() {
        this.stagedEnabled = {}
        this.stagedPriority = {}
        this.priorityDrafts = {}
        this.moved = []
      },
      async saveChanges() {
        const changes = this.changes
        if (!changes.length) return
        const snackbar = useSnackbarStore()
        this.isSaving = true
        try {
          const { failed } = await AlertsBannersMutator.updateBanners(
            changes.map(({ group, patch }) => ({
              rowIds: group.rowIds,
              patch,
            })),
          )
          if (!failed.length) {
            snackbar.addSuccess(
              "Banners saved. Customers see the changes within about a minute.",
            )
            this.discardChanges()
            return
          }
          const failedIds = new Set(failed.map((f) => f.id))
          const failedNames = changes
            .filter(({ group }) => group.rowIds.some((id) => failedIds.has(id)))
            .map(({ group }) => group.name)
          snackbar.addError(
            failedNames.length === 1
              ? `Could not save ${failedNames[0]}: ${failed[0].message}`
              : `Could not save ${failedNames.join(", ")}`,
          )
        } finally {
          this.isSaving = false
          this.confirming = false
        }
      },
    },
  })
</script>
<style scoped>
  .banner-table :deep(tbody > tr > td.v-data-table__td) {
    height: 96px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .banner-cell {
    min-width: 240px;
    max-width: 420px;
  }
  .button-cell {
    max-width: 240px;
  }
  .one-line {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .two-lines {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }
  .banner-table :deep(tbody > tr:has(.banner-changed) > td.v-data-table__td) {
    background-color: rgba(var(--v-theme-warning), 0.3);
    transition: background-color 0.2s;
  }
  .drag-handle {
    display: flex;
    cursor: grab;
    color: rgb(var(--v-theme-disabled));
  }
  .drag-handle:hover {
    color: rgb(var(--v-theme-inverted));
  }
  .banner-table :deep(tbody > tr:has(.banner-dragging) > td.v-data-table__td) {
    opacity: 0.5;
  }
  .heading-row {
    min-height: 48px;
  }
  .priority-field {
    width: 64px;
  }
</style>
